import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { backendEndPointToken } from '../../app.config';
import { StateService } from '../state.service';
import { UtilityService } from '../utility.service';
import { ActivityDefinition, ChargeItem, ChargeItemDefinition, Observation, Procedure, ServiceRequest, SupplyDelivery, ImagingStudy, Invoice } from 'fhir/r4';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { inferServiceRequestType, ServiceRequestType } from '../service-request-type.util';
import { referenceMatches, referenceArrayContains } from '../fhir-reference.util';
import { extractItemsRequiredRows } from '../activity-definition-items-required';

export interface PricingCalculationResult {
  serviceRequestId: string;
  serviceRequestCode: string;
  serviceRequestDisplay: string;
  serviceRequestType: ServiceRequestType;

  // Consumables item costs
  requiredConsumablesCost: number;
  consumables: Array<{
    code: string;
    display: string;
    requiredQuantity: number;
    resolvedUnitCost: number;
    totalCost: number;
    matchedSupplyDeliveryId: string | null;
  }>;

  // Service base cost
  baseCost: number;
  chargeItemDefinitionId: string | null;

  // Selling price & profit
  sellingPrice: number;
  netProfit: number;
  currency: string;

  // Traversal details
  chargeItemId: string | null;
  clinicalPlaceholderId: string | null;
  clinicalPlaceholderType: string | null;
}

@Injectable({
  providedIn: 'root'
})
export class ClinicalPricingService {
  private readonly http = inject(HttpClient);
  private readonly backendEndPoint = inject(backendEndPointToken);
  private readonly stateService = inject(StateService);
  private readonly utilityService = inject(UtilityService);

  /**
   * Helper to retrieve service request code
   */
  private getServiceRequestCode(sr: ServiceRequest): string | null {
    const code = (sr as any)?.code?.coding?.[0]?.code ?? (sr as any)?.code?.text ?? null;
    return code ? code.toString().trim() : null;
  }

  /**
   * Helper to parse ID from canonical url
   */
  private parseIdFromCanonical(canonical: any, expectedType: string): string | null {
    if (!canonical || typeof canonical !== 'string') return null;
    const raw = canonical.toString().trim();
    if (!raw) return null;

    const noFrag = raw.split('#')[0].split('?')[0];
    const parts = noFrag.split('/').filter(Boolean);
    if (parts.length < 2) return null;

    const id = parts[parts.length - 1];
    const type = parts[parts.length - 2];
    if (type !== expectedType) return null;
    return id || null;
  }

  /**
   * Pick the best ActivityDefinition match from list of matching code definitions
   */
  private pickBestActivityDefinition(defs: any[], code: string): any | null {
    const matches = (defs || []).filter((d: any) => (d?.code as any)?.coding?.[0]?.code === code);
    const withTopic = matches.find((d: any) =>
      (d?.topic || []).some((t: any) => ((t?.text ?? '').toString().toLowerCase()).includes('items usage computation'))
    );
    return withTopic ?? matches[0] ?? null;
  }

  /**
   * Step 1: Resolve the ActivityDefinition associated with the ServiceRequest
   */
  resolveActivityDefinition(serviceRequest: ServiceRequest): Observable<ActivityDefinition | null> {
    const inst = serviceRequest.instantiatesCanonical;
    const canonical = Array.isArray(inst) ? inst[0] : inst;
    const instanceId = this.parseIdFromCanonical(canonical, 'ActivityDefinition');

    if (instanceId) {
      const localAd = (this.stateService.orgWideResources.activityDefinitions.getValue() || [])
        .map(w => w.actualResource)
        .find(ad => ad.id === instanceId);

      if (localAd) return of(localAd);
      return this.http.get<ActivityDefinition>(`${this.backendEndPoint}/ActivityDefinition/${instanceId}`).pipe(
        catchError(() => of(null))
      );
    }

    const code = this.getServiceRequestCode(serviceRequest);
    if (!code) return of(null);

    const localDefs = (this.stateService.orgWideResources.activityDefinitions.getValue() || [])
      .map(w => w.actualResource);

    const localMatch = this.pickBestActivityDefinition(localDefs, code);
    if (localMatch) return of(localMatch);

    // Fallback: search backend
    return this.utilityService.threeTierQueryAndSyncToStateOnce$<any>({
      resourceType: 'ActivityDefinition',
      urlSearchObject: {
        status: 'active',
      },
      querySearchObject: [
        { key: 'status', value: 'active' },
        { key: 'code.coding.code', value: code }
      ],
      scope: 'orgWide',
      count: 10
    }).pipe(
      map(fresh => this.pickBestActivityDefinition(fresh, code)),
      catchError(() => of(null))
    );
  }

  /**
   * Step 3: Traverse the Round-Round chain to locate the associated ChargeItem
   */
  resolveChargeItemForServiceRequest(
    serviceRequest: ServiceRequest,
    patientId: string
  ): Observable<{ chargeItem: ChargeItem | null; clinicalPlaceholderId: string | null; clinicalPlaceholderType: string | null; invoice: Invoice | null }> {
    const srId = serviceRequest.id;
    if (!srId) return of({ chargeItem: null, clinicalPlaceholderId: null, clinicalPlaceholderType: null, invoice: null });
    const srRef = `ServiceRequest/${srId}`;
    const srType = inferServiceRequestType(serviceRequest);

    let placeholders$: Observable<any[]>;
    let placeholderType: string | null = null;

    if (srType === 'laboratory') {
      placeholderType = 'Observation';
      const localObs = (this.stateService.PatientResources.observations.getValue() || [])
        .map(w => w.actualResource)
        .filter((o: any) => referenceArrayContains(o?.basedOn, srRef));

      // placeholders$ = localObs.length 
      //   ? of(localObs)
      //   : 
      placeholders$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<Observation>({
        resourceType: 'Observation',
        urlSearchObject: { subject: `Patient/${patientId}`, 'based-on': srRef },
        querySearchObject: [
          { key: 'basedOn.reference', value: srRef },
          { key: 'subject.reference', value: [`Patient/${patientId}`, `${this.backendEndPoint}/Patient/${patientId}`] }
        ],
        patientId,
        scope: 'patient',
        tier1MaxPages: 10,
        expectedMinLength: 0,
        tier1FollowNextLinks: true
      }).pipe(
        map(list => (list || []).filter((o: any) => referenceArrayContains(o?.basedOn, srRef))),
        catchError(() => of([]))
      );
    } else if (srType === 'imaging') {
      placeholderType = 'ImagingStudy';
      const localImaging = (this.stateService.PatientResources.imagingStudies.getValue() || [])
        .map(w => w.actualResource)
        .filter((img: any) => referenceArrayContains(img?.basedOn, srRef));

      // placeholders$ = localImaging.length
      //   ? of(localImaging)
      //   :
      placeholders$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<ImagingStudy>({
        resourceType: 'ImagingStudy',
        urlSearchObject: { patient: `Patient/${patientId}`, basedon: srRef },
        querySearchObject: [
          { key: 'basedOn.reference', value: srRef },
          { key: 'subject.reference', value: [`Patient/${patientId}`, `${this.backendEndPoint}/Patient/${patientId}`] }
        ],
        patientId,
        scope: 'patient',
        tier1MaxPages: 10,
        expectedMinLength: 0,
        tier1FollowNextLinks: true
      }).pipe(
        map(list => (list || []).filter((img: any) => referenceArrayContains(img?.basedOn, srRef))),
        catchError(() => of([]))
      );
    } else if (srType === 'procedure' || srType === 'surgery') {
      placeholderType = 'Procedure';
      const localProcs = (this.stateService.PatientResources.procedures.getValue() || [])
        .map(w => w.actualResource)
        .filter((p: any) => referenceArrayContains(p?.basedOn, srRef));

      // placeholders$ = localProcs.length
      //   ? of(localProcs)
      //   : 
      placeholders$ = this.utilityService.threeTierQueryAndSyncToStateOnce$<Procedure>({
        resourceType: 'Procedure',
        urlSearchObject: { subject: `Patient/${patientId}`, 'based-on': srRef },
        querySearchObject: [
          { key: 'basedOn.reference', value: srRef },
          { key: 'subject.reference', value: [`Patient/${patientId}`, `${this.backendEndPoint}/Patient/${patientId}`] }
        ],
        patientId,
        scope: 'patient',
        expectedMinLength: 0,
        tier1FollowNextLinks: true,
        tier1MaxPages: 10,
      }).pipe(
        map(list => (list || []).filter((p: any) => referenceArrayContains(p?.basedOn, srRef))),
        catchError(() => of([]))
      );
    } else {
      placeholders$ = of([]);
    }

    const localChargeItems = (this.stateService.PatientResources.chargeItems.getValue() || [])
      .map(w => w.actualResource);

    // const chargeItems$: Observable<ChargeItem[]> = localChargeItems.length
    //   ? of(localChargeItems)
    //   :
    const chargeItems$: Observable<ChargeItem[]> = this.utilityService.threeTierQueryAndSyncToStateOnce$<ChargeItem>({
      resourceType: 'ChargeItem',
      urlSearchObject: { subject: `Patient/${patientId}` },
      querySearchObject: [
        { key: 'subject.reference', value: [`Patient/${patientId}`, `${this.backendEndPoint}/Patient/${patientId}`] }
      ],
      patientId,
      scope: 'patient',
      tier1MaxPages: 10,
      expectedMinLength: 0,
      tier1FollowNextLinks: true
    }).pipe(
      catchError(() => of([] as ChargeItem[]))
    );

    const localInvoices = (this.stateService.PatientResources.invoices.getValue() || [])
      .map(w => w.actualResource);

    // const invoices$: Observable<Invoice[]> = localInvoices.length
    //   ? of(localInvoices)
    //   : 
    const invoices$: Observable<Invoice[]> = this.utilityService.threeTierQueryAndSyncToStateOnce$<Invoice>({
      resourceType: 'Invoice',
      urlSearchObject: { subject: `Patient/${patientId}` },
      querySearchObject: [
        {
          key: 'subject.reference', value: [`Patient/${patientId}`, `${this.backendEndPoint}/Patient/${patientId}`]
        }
        //balanced invoices only, since those are the ones that have completed the billing cycle and reflect actual prices paid/billed. Draft or issued invoices may not have final prices.
      ],
      patientId,
      scope: 'patient',
      tier1MaxPages: 10,
      expectedMinLength: 0,
      tier1FollowNextLinks: true
    }).pipe(
      catchError(() => of([] as Invoice[]))
    );

    return forkJoin({ placeholders: placeholders$, chargeItems: chargeItems$, invoices: invoices$ }).pipe(
      map(({ placeholders, chargeItems, invoices }) => {
        let matchedChargeItem: ChargeItem | null = null;
        let matchedPlaceholderId: string | null = null;

        if (srType === 'others') {
          // Direct match: ChargeItem.service contains ServiceRequest/{id}
          matchedChargeItem = chargeItems.find((ci: any) => {
            const services = Array.isArray(ci?.service) ? ci.service : [];
            return services.some((s: any) => referenceMatches(s?.reference, srRef));
          }) ?? null;
        } else if (placeholders.length > 0) {
          // Indirect match: find ChargeItem that references any of the placeholders
          for (const placeholder of placeholders) {
            const pRef = `${placeholderType}/${placeholder.id}`;
            const found = chargeItems.find((ci: any) => {
              const services = Array.isArray(ci?.service) ? ci.service : [];
              return services.some((s: any) => referenceMatches(s?.reference, pRef));
            });
            if (found) {
              matchedChargeItem = found;
              matchedPlaceholderId = placeholder.id ?? null;
              break;
            }
          }

          if (!matchedPlaceholderId) {
            matchedPlaceholderId = placeholders[0].id ?? null;
          }
        }

        let matchedInvoice: Invoice | null = null;
        if (matchedChargeItem && matchedChargeItem.id) {
          const targetRef = `ChargeItem/${matchedChargeItem.id}`;
          matchedInvoice = (invoices || []).find((inv: any) => {
            const status = (inv?.status || '').toString().toLowerCase();
            if (status !== 'balanced') return false;
            const lineItems = inv?.lineItem || [];
            return lineItems.some((li: any) => {
              const ref = (li?.chargeItemReference?.reference || '').toString();
              return ref === targetRef || ref.endsWith(`/${targetRef}`) || ref.endsWith(`/${matchedChargeItem!.id}`);
            });
          }) ?? null;
        }

        ///* alert (`For ServiceRequest ${srId}, matched ChargeItem: ${JSON.stringify(matchedChargeItem?.extension?? 'none') */ }, matched Placeholder: ${matchedPlaceholderId ?? 'none'}, matched Invoice: ${matchedInvoice?.id ?? 'none'}`);

        return {
          chargeItem: matchedChargeItem,
          clinicalPlaceholderId: matchedPlaceholderId,
          clinicalPlaceholderType: placeholderType,
          invoice: matchedInvoice
        };
      })
    );
  }

  /**
   * Step 4: Resolve ChargeItemDefinition from definitionCanonical or code-matching
   */
  resolveChargeItemDefinition(
    chargeItem: ChargeItem | null,
    serviceRequest: ServiceRequest
  ): Observable<ChargeItemDefinition | null> {
    const cidCanonical = chargeItem?.definitionCanonical;
    // if (cidCanonical) { // the app disables this path for now, because we want to rely on code-matching instead of canonical matching
    if (false) {
      const cidId = this.parseIdFromCanonical(cidCanonical, 'ChargeItemDefinition');
      if (cidId) {
        const localCid = (this.stateService.orgWideResources.chargeItemDefinitions.getValue() || [])
          .map(w => w.actualResource)
          .find(cid => cid.id === cidId);

        // if (localCid) return of(localCid);
        return this.http.get<ChargeItemDefinition>(`${this.backendEndPoint}/ChargeItemDefinition/${cidId}`).pipe(
          catchError(() => of(null))
        );
      }
    }

    const serviceCode = serviceRequest?.code?.coding?.[0]?.code ?? this.getServiceRequestCode(serviceRequest);
// $#$    // /* alert(serviceCode); */
    if (!serviceCode) return of(null);

    const localCidByCode = (this.stateService.orgWideResources.chargeItemDefinitions.getValue() || [])
      .map(w => w.actualResource)
      .find(cid => {

        return (cid.code?.coding?.[0]?.code === serviceCode || cid.code?.text === serviceCode)
          && (
            cid.status === 'active'

          )
      });

    if (localCidByCode) return of(localCidByCode);

    return this.utilityService.threeTierQueryAndSyncToStateOnce$<ChargeItemDefinition>({
      resourceType: 'ChargeItemDefinition',
      urlSearchObject: { status: 'active' },
      querySearchObject: [
        { key: 'code.coding.code', value: serviceCode },
        { key: 'status', value: 'active' }
      ],
      tier1FollowNextLinks: true,
      scope: 'orgWide',
      tier1MaxPages: 100,
      count: 1
    }).pipe(
      map(list => {
// $#$        // /* alert(`For ServiceRequest ${serviceRequest.id}, found ${list?.length ?? 0} ChargeItemDefinition(s) */ matching code: ${serviceCode}`);
        return list?.[0] ?? null
      }),
      catchError(() => of(null))
    );
  }

  /**
   * Main Standby Calculation API: Computes clinical pricing and margins for a ServiceRequest
   */
  calculatePricingForServiceRequest(
    serviceRequest: ServiceRequest,
    patientId: string
  ): Observable<PricingCalculationResult> {
    const srId = (serviceRequest.id ?? '').toString();
    const srCode = this.getServiceRequestCode(serviceRequest) ?? '';
    const srDisplay = serviceRequest.code?.text ?? serviceRequest.code?.coding?.[0]?.display ?? srCode;
    const srType = inferServiceRequestType(serviceRequest);

    return this.resolveActivityDefinition(serviceRequest).pipe(
      switchMap(activityDef => {
        if (!activityDef) {
          return of({ activityDef: null, consumablesCost: 0, consumables: [] as any[] });
        }

        const localDeliveries = (this.stateService.orgWideResources.supplyDeliveries.getValue() || [])
          .map(w => w.actualResource);

        const deliveries$: Observable<SupplyDelivery[]> = localDeliveries.length
          ? of(localDeliveries)
          : this.utilityService.threeTierQueryAndSyncToStateOnce$<SupplyDelivery>({
            resourceType: 'SupplyDelivery',
            urlSearchObject: { status: 'completed' },
            querySearchObject: [
              { key: 'status', value: 'completed' }
            ],
            scope: 'orgWide',
            count: 1000
          }).pipe(
            catchError(() => of([] as SupplyDelivery[]))
          );

        return deliveries$.pipe(
          map(allDeliveries => {
            const rows = extractItemsRequiredRows(activityDef);

            let totalConsumablesCost = 0;
            const consumablesList: any[] = [];

            for (const row of rows) {
              const rowItem = row.items;
              const qty = row.quantity ?? 0;
// $#$              ///* alert(`Processing row: ${JSON.stringify(row) */} with quantity: ${qty}`);
              const coding0 = rowItem?.coding?.[0];
              const substanceCode = coding0?.code ?? rowItem?.text ?? '';
              const substanceDisplay = coding0?.display ?? rowItem?.text ?? substanceCode;

              let resolvedUnitCost = 0;
              let totalCost = 0;
              let matchedSupplyDeliveryId: string | null = null;

              const isMatchingSupplyDelivery = (sd: SupplyDelivery, targetCode: string): boolean => {
                if (!sd || !targetCode) return false;
                const tcLower = targetCode.toLowerCase();
                const codeableConcept = sd.suppliedItem?.itemCodeableConcept;
                if (codeableConcept) {
                  if (codeableConcept.text?.toLowerCase() === tcLower) return true;
                  const codings = codeableConcept.coding || [];
                  for (const c of codings) {
                    if (c.code?.toLowerCase() === tcLower || c.display?.toLowerCase() === tcLower) {
                      return true;
                    }
                  }
                }
                const tags = sd.meta?.tag || [];
                for (const t of tags) {
                  if (t.code?.toLowerCase() === tcLower || t.display?.toLowerCase() === tcLower) {
                    return true;
                  }
                }
                return false;
              };

              const completedDeliveries = allDeliveries.filter(sd => sd.status === 'completed' && isMatchingSupplyDelivery(sd, substanceCode));
              if (completedDeliveries.length > 0) {
// $#$                ///* alert(`Found ${completedDeliveries.length} completed SupplyDelivery records for substance code: ${substanceCode}`); */
                completedDeliveries.sort((a, b) => {
                  const timeA = new Date(a.occurrenceDateTime || a.meta?.lastUpdated || 0).getTime();
                  const timeB = new Date(b.occurrenceDateTime || b.meta?.lastUpdated || 0).getTime();
                  return timeB - timeA;
                });

                const latestDelivery = completedDeliveries[0];
                matchedSupplyDeliveryId = latestDelivery.id ?? null;

                const costPriceExt = latestDelivery.extension?.find(e => e.url === 'http://hl7.org/fhir/StructureDefinition/totalCostPrice' || e.url?.endsWith('/totalCostPrice'));
                const totalAmountExt = latestDelivery.extension?.find(e => e.url === 'http://hl7.org/fhir/StructureDefinition/totalAmount' || e.url?.endsWith('/totalAmount'));

                const totalCostVal = costPriceExt?.valueMoney?.value ?? 0;
                const totalAmountVal = totalAmountExt?.valueQuantity?.value ?? 0;

                if (totalAmountVal > 0) {
                  resolvedUnitCost = totalCostVal / totalAmountVal;
                }
// $#$                ///* alert(`Resolved unit cost for ${substanceCode} is ${resolvedUnitCost} (totalCost: ${totalCostVal}, totalAmount: ${totalAmountVal}) */`);
                totalCost = qty * resolvedUnitCost;
              }

              totalConsumablesCost += totalCost;
              consumablesList.push({
                code: substanceCode,
                display: substanceDisplay,
                requiredQuantity: qty,
                resolvedUnitCost,
                totalCost,
                matchedSupplyDeliveryId
              });
            }
// $#$            ///* alert(`Total consumables cost for service request ${srId} is ${totalConsumablesCost}`); */
            return {
              activityDef,
              consumablesCost: totalConsumablesCost,
              consumables: consumablesList
            };
          })
        );
      }),
      switchMap(({ activityDef, consumablesCost, consumables }) => {
        return this.resolveChargeItemForServiceRequest(serviceRequest, patientId).pipe(
          switchMap(({ chargeItem, clinicalPlaceholderId, clinicalPlaceholderType, invoice }) => {

            return this.resolveChargeItemDefinition(chargeItem, serviceRequest).pipe(

              map(cid => {
// $#$                // ///* alert(cid); */
                let baseCost = 0;
                let sellingPrice = 0;
                let currency = 'NGN';
                let cidId: string | null = null;

                if (cid) {
                  cidId = cid.id ?? null;


                  const totalCostExts = cid.extension?.filter(ext =>
                    ext.url === 'http://hl7.org/fhir/StructureDefinition/ChargeItemDefinition-totalCost' || ext.url?.endsWith('/ChargeItemDefinition-totalCost')
                  ) || [];

                  let totalBase = 0;
                  let totalSurcharge = 0;
                  let totalTax = 0;
                  let totalDeduction = 0;
                  let totalDiscount = 0;

                  totalCostExts.forEach(ext => {
// $#$                    ///* alert(JSON.stringify(ext) */ + serviceRequest.id);
                    const subs = ext.extension || [];
                    const typeSub = subs.find(e => e.url === 'type');
                    const amountSub = subs.find(e => e.url === 'amount');
                    if (typeSub && amountSub) {
                      const type = (typeSub as any).valueCode as string;
                      const money = (amountSub as any).valueMoney;
                      if (money && typeof money.value === 'number') {
                        const amount = money.value;
                        switch (type?.toLowerCase()) {
                          case 'base':
                            totalBase += amount;
                            break;
                          case 'surcharge':
                            totalSurcharge += amount;
                            break;
                          case 'tax':
                            totalTax += amount;
                            break;
                          case 'deduction':
                            totalDeduction += amount;
                            break;
                          case 'discount':
                            totalDiscount += amount;
                            break;
                        }
                      }
                    }
                  });

                  // The professional service cost is precisely the 'base' component
                  baseCost = totalBase;

                  const sellingComponents = this.utilityService.seperateTotalPriceComponents(cid);
                  if (sellingComponents) {
                    sellingPrice = sellingComponents.totalSellingPrice ?? 0;
                    currency = sellingComponents.currency ?? 'NGN';
                  }
                }

                // Fallback / standard override: actual transaction-level price on the ChargeItem
                if (chargeItem?.priceOverride?.value !== undefined && typeof chargeItem.priceOverride.value === 'number') {
                  sellingPrice = chargeItem.priceOverride.value;
                  if (chargeItem.priceOverride.currency) {
                    currency = chargeItem.priceOverride.currency;
                  }
                }

                // Ultimate priority: actual transaction-level price paid/billed on the balanced Invoice
                if (invoice && chargeItem && chargeItem.id) {
                  const targetRef = `ChargeItem/${chargeItem.id}`;
                  const matchedLineItem = invoice.lineItem?.find((li: any) => {
                    const ref = (li?.chargeItemReference?.reference || '').toString();
                    return ref === targetRef || ref.endsWith(`/${targetRef}`) || ref.endsWith(`/${chargeItem.id}`);
                  });
                  if (matchedLineItem) {
                    const priceComponents = matchedLineItem.priceComponent || [];
                    if (priceComponents.length > 0) {
                      let base = 0, surcharge = 0, tax = 0, deduction = 0, discount = 0;
                      priceComponents.forEach((pc: any) => {
                        const type = (pc.type || '').toString().trim().toLowerCase();
                        const value = Number(pc.amount?.value) || 0;
                        if (pc.amount?.currency) currency = pc.amount.currency;
                        if (type === 'base') base += value;
                        else if (type === 'surcharge') surcharge += value;
                        else if (type === 'tax') tax += value;
                        else if (type === 'deduction') deduction += value;
                        else if (type === 'discount') discount += value;
                      });
                      sellingPrice = (base + surcharge + tax) - Math.abs(deduction) - Math.abs(discount);
                    }
                  }
                }

                const qty = chargeItem?.quantity?.value || 1;
                const totalBaseCost = baseCost * qty;
                const totalConsumablesCost = consumablesCost * qty;

                // If selling price from invoice/chargeItem was unit price, we multiply by qty. 
                // However, usually invoice line item price component amount is unit price, or total price? 
                // To be safe, if we use priceOverride or invoice components, we should multiply by qty.
                const totalSellingPrice = sellingPrice * qty;

                const totalCostPrice = totalConsumablesCost + totalBaseCost;
// $#$                ///* alert(`For ServiceRequest ${srId}, totalBaseCost: ${totalBaseCost}, totalConsumablesCost: ${totalConsumablesCost}, totalSellingPrice: ${totalSellingPrice}`); */
                const netProfit = totalSellingPrice - totalCostPrice;

                return {
                  serviceRequestId: srId,
                  serviceRequestCode: srCode,
                  serviceRequestDisplay: srDisplay,
                  serviceRequestType: srType,

                  requiredConsumablesCost: totalConsumablesCost,
                  consumables,

                  baseCost: totalBaseCost,
                  chargeItemDefinitionId: cidId,

                  sellingPrice: totalSellingPrice,
                  netProfit,
                  currency,

                  chargeItemId: chargeItem?.id ?? null,
                  clinicalPlaceholderId,
                  clinicalPlaceholderType
                };
              })
            );
          })
        );
      })
    );
  }
}
